import React, { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PanelBar, PanelBarItem } from "@progress/kendo-react-layout";
import _ from "lodash";
import { getTimezones } from "../../../../../actions/userProfile";
import { setExpanded } from "../../../../../actions/userProfileSidebar";
import { userProfileNavbarOptions } from "./userProfileSidebar.constants";
import { useProfileContext } from "../UserProfile.context";
import PersonalInformationSection from "./PersonalInformationSection";
import LoginSettingsSection from "./LoginSettingsSection";
import WorkInformationSection from "./WorkInformationSection";
import BoardStagesSection from "./BoardStagesSection";
import EmailNotificationsSection from "./EmailNotificationsSection";
import OtherSettingsSection from "./OtherSettingsSection";
import TeamActivityLog from "../../../../../components/TeamSetting/TeamActivityLog/TeamActivityLog";
import { useGetUser } from "../../../../../components/UserProfileSetting/hooks/userProfileHelper.hooks";
import "./UserProfileSidebarContent.scss";

/**
 * Handles the collapsible sections using Kendo PanelBar
 * @author Bhavana
 */
const UserProfileSidebarContent = () => {
  const dispatch = useDispatch();
  const { expanded, focusedElement } = useSelector((state) => state.userProfileSidebar || { expanded: [], focusedElement: null });
  const { setTimeZoneList, setInitialTimeZone } = useProfileContext();
  const [timezoneNames, setTimezoneNames] = useState([]);
  const user = useGetUser();
  const { myProjectId } = user;

  /**
   * Fetches timezones and sets the timeZoneList state.
   * @author Bhavana
   */
  useEffect(() => {
    (async () => {
      let timezoneListResponse = await dispatch(getTimezones());
      if (timezoneListResponse) {
        setTimezoneNames(timezoneListResponse);
        setTimeZoneList(timezoneListResponse);
        setInitialTimeZone(timezoneListResponse);
      }
    })();
  }, []);

  /**
   * Sets expanded state of PanelBar on initial render
   * Based on expandCondition functions
   * Excludes profile-personal-info as it's now outside PanelBar
   * Uses a thunk to read the live Redux state at dispatch time so that items already
   * expanded by external triggers (e.g. UrlWorkDigestSidebar) are preserved rather
   * than overwritten by this initialisation reset
   * @author Bhavana
   */
  useEffect(() => {
    const panelBarOptions = userProfileNavbarOptions?.filter((option) => option.id !== "profile-personal-info");
    const expandedItems = panelBarOptions?.filter((option) => option.expandCondition());
    let result = expandedItems.map((ele) => ele.id);
    dispatch((thunkDispatch, getState) => {
      const currentExpanded = getState().userProfileSidebar?.expanded || [];
      thunkDispatch(setExpanded(_.uniq([...currentExpanded, ...result])));
    });
  }, []);

  /**
   * Handles PanelBar selection (expand/collapse)
   * @param {Object} event - Kendo PanelBar event
   * @author Bhavana
   */
  const handleSelect = (event) => {
    if (event.expandedItems) {
      dispatch(setExpanded(event.expandedItems));
    }
  };

  /**
   * Returns component content based on section ID
   * @param {String} id - Section ID
   * @returns {JSX.Element}
   * @author Bhavana
   */
  const getComponent = (id) => {
    switch (id) {
      case "profile-personal-info":
        return <PersonalInformationSection />;
      case "profile-login-options":
        return <LoginSettingsSection />;
      case "profile-work-information":
        return <WorkInformationSection />;
      case "profile-board-stages":
        return <BoardStagesSection />;
      case "profile-email-notifications":
        return <EmailNotificationsSection />;
      case "profile-other-settings":
        return <OtherSettingsSection />;
      case "profile-logs":
        return (
          <div className="profile-section-content bg-light mt-1">
            <TeamActivityLog projectId={myProjectId} />
          </div>
        );
      default:
        return null;
    }
  };

  // Filter out profile-personal-info from PanelBar options
  const panelBarOptions = userProfileNavbarOptions.filter((option) => option.id !== "profile-personal-info");

  return (
    <div className="user-profile-sidebar-content">
      {/* PersonalInformationSection - Always visible, outside PanelBar */}
      <PersonalInformationSection />

      {/* PanelBar with other collapsible sections */}
      {panelBarOptions
        .map((option, idx) => {
          const show = option.renderCondition(option.render);
          if (show) {
            const elementLabel = option.label instanceof Function ? option.label() : option.label;
            return (
              <PanelBar
                key={idx}
                isControlled={true}
                expanded={expanded}
                onSelect={handleSelect}
                expandMode="multiple"
                className="user-profile-panelbar">
                <PanelBarItem
                  key={idx.toString()}
                  id={option.id}
                  title={<div className="panelbar-item-title d-flex align-items-center justify-content-between w-100 font-weight-medium">{elementLabel}</div>}
                  className="user-profile-panelbar-item">
                  {getComponent(option.id)}
                </PanelBarItem>
              </PanelBar>
            );
          }
          return null;
        })
        .filter(Boolean)}
    </div>
  );
};

export default UserProfileSidebarContent;

